
export const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
};

export const base64ToFile = (base64: string, filename: string, mimeType: string): File => {
  const byteString = atob(base64);
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  const blob = new Blob([ab], { type: mimeType });
  return new File([blob], filename, { type: mimeType });
};

let CLOUD_NAME = 'dkphtyjeo';
let UPLOAD_PRESET = 'meitai';

try {
  CLOUD_NAME = process.env.CLOUDINARY_CLOUD_NAME || 'dkphtyjeo';
  UPLOAD_PRESET = process.env.CLOUDINARY_UPLOAD_PRESET || 'meitai';
} catch (e) {
  // Ignore
}

export const uploadToCloudinary = async (file: File): Promise<string> => {
  // 1. 严格检查配置
  if (!CLOUD_NAME || !UPLOAD_PRESET) {
      const msg = "Configuration Error: Cloudinary configuration is missing. Uploads are disabled to prevent database overflow.";
      alert(msg);
      throw new Error(msg);
  }

  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', UPLOAD_PRESET);

  try {
    const response = await fetch(`https://api.cloudinary.com/v1_1/${CLOUD_NAME}/auto/upload`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error("Cloudinary Upload Failed:", errorData);
      
      // 2. 上传失败弹出提示
      const msg = `Image Upload Failed: ${errorData.error?.message || 'Server rejected the image'}.`;
      alert(msg);
      throw new Error(msg);
    }

    const data = await response.json();
    console.log("Cloudinary Upload Success:", data.secure_url);
    
    // 3. 自动注入 f_auto,q_auto 优化参数
    // 这确保了从移动设备上传的 HEIC 等格式能被浏览器正确显示，并优化加载速度
    let url = data.secure_url;
    if (url && url.includes('/upload/') && !url.includes('f_auto')) {
        url = url.replace('/upload/', '/upload/f_auto,q_auto/');
    }
    
    return url;

  } catch (error: any) {
    // 3. 网络错误弹出提示
    // 避免重复弹窗（如果上面已经抛出带消息的Error）
    if (!error.message?.includes("Upload Failed") && !error.message?.includes("Configuration Error")) {
        console.error("Network error during upload.", error);
        alert("Network Error: Could not connect to image server. Please check your internet connection.");
    }
    
    // 4. 严禁降级到 fileToBase64，直接抛出异常中断流程
    throw error;
  }
};

/**
 * 核心调整：统一图片优化逻辑
 * 1. 如果是 Cloudinary 链接，确保包含 f_auto,q_auto
 * 2. 如果是本地路径且在生产环境，通过 Cloudinary Fetch API 进行实时优化
 * 3. 解决“存放位置与上传位置矛盾”的问题：让所有图片都享受云端加速
 */
export const getOptimizedUrl = (url: string): string => {
  if (!url) return '';
  
  // 1. 处理已经是 Cloudinary 的链接
  if (url.includes('res.cloudinary.com')) {
    if (url.includes('/upload/') && !url.includes('f_auto')) {
      return url.replace('/upload/', '/upload/f_auto,q_auto/');
    }
    return url;
  }

  // 2. 处理本地路径 (例如 /images/xxx.webp)
  // 只有在非本地开发环境且配置了 Cloudinary 时才启用 Fetch 优化
  const isLocalPath = url.startsWith('/');
  const cloudName = CLOUD_NAME;
  
  if (isLocalPath && cloudName && typeof window !== 'undefined' && !window.location.hostname.includes('localhost')) {
    const origin = window.location.origin;
    const absoluteUrl = `${origin}${url}`;
    // 使用 Cloudinary Fetch API: https://cloudinary.com/documentation/fetch_remote_images
    return `https://res.cloudinary.com/${cloudName}/image/fetch/f_auto,q_auto/${absoluteUrl}`;
  }

  return url;
};
